import type { RunStepOut, RunTokensOut } from "@platform/api/golden";

/** Dollars per million tokens. */
interface ShelfPrice {
  readonly input: number;
  readonly output: number;
  readonly cacheRead: number;
  readonly cacheWrite: number;
}

// Anthropic's list prices as of 2026-10-06. A cache write is priced as a
// 5-minute write, 1.25 times the input price.
const SHELF_PRICES: Readonly<Record<string, ShelfPrice>> = {
  "claude-opus-5-5": { input: 4, output: 20, cacheRead: 0.2, cacheWrite: 5 },
  "claude-sonnet-5-5": {
    input: 2,
    output: 10,
    cacheRead: 0.2,
    cacheWrite: 2.5,
  },
  // OpenAI's list prices for GPT-6.1 Sol as of 2026-10-29, keyed by its Foundry
  // deployment name. A cache write is priced as a 30-minute write. Prompts over
  // 272K input tokens cost more, which this ignores.
  "gpt-6-1-sol": { input: 2, output: 10, cacheRead: 0.1, cacheWrite: 2.5 },
};

// RUMI runs on Azure AI Foundry, which costs about 10% more than the shelf price.
const FOUNDRY_MARKUP = 1.1;

/**
 * What tokens cost at the model's shelf price plus Foundry's markup, in
 * dollars; undefined for a model with no shelf price here.
 */
export const tokenCost = (
  model: string,
  tokens: RunTokensOut,
): number | undefined => {
  const price = SHELF_PRICES[model];
  if (!price) return undefined;
  // The runner reports pydantic-ai's input count, which includes the cache
  // reads and writes (rumi-runner's run_events.py and llmobs.py).
  const uncachedInput = tokens.input - tokens.cache_read - tokens.cache_write;
  const perMillion =
    uncachedInput * price.input +
    tokens.output * price.output +
    tokens.cache_read * price.cacheRead +
    tokens.cache_write * price.cacheWrite;
  return (perMillion / 1_000_000) * FOUNDRY_MARKUP;
};

/** What the run's model turns cost, as tokenCost prices them. */
export const estimatedCost = (
  model: string,
  steps: readonly RunStepOut[],
): number | undefined =>
  tokenCost(
    model,
    steps.reduce<RunTokensOut>(
      (total, { kind, usage }) =>
        kind === "model_turn" && usage
          ? {
              input: total.input + usage.input,
              output: total.output + usage.output,
              cache_read: total.cache_read + usage.cache_read,
              cache_write: total.cache_write + usage.cache_write,
            }
          : total,
      { input: 0, output: 0, cache_read: 0, cache_write: 0 },
    ),
  );
